import { useEffect, useState } from "react";
import client from "../lib/api";
import { money, fmtDate } from "../lib/helpers";
import { HandCoins, CheckCircle, Clock, CalendarStar } from "@phosphor-icons/react";
import { Badge } from "./ui/badge";

function Stat({ label, value, icon: Icon, tone }) {
  return (
    <div className="surface-kpi p-6 flex flex-col gap-3">
      <div className="flex items-center gap-2 text-[#5C5A58] eyebrow relative z-10"><Icon size={18} weight="duotone" color={tone} />{label}</div>
      <p className="font-display text-3xl tabular-nums relative z-10" style={{ color: tone }}>{money(value)}</p>
    </div>
  );
}

export default function CommissionsTracker() {
  const [data, setData] = useState(null);

  useEffect(() => { client.get("/finance/commissions").then((r) => setData(r.data)).catch(() => setData({ rows: [], by_photographer: [], total: 0, paid: 0, unpaid: 0, month: 0, month_label: "" })); }, []);

  if (!data) return <p className="text-[#5C5A58]">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="commissions-tracker">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Stat label="Total commissions" value={data.total} icon={HandCoins} tone="#4A0000" />
        <Stat label="Paid out" value={data.paid} icon={CheckCircle} tone="#2e7d32" />
        <Stat label="Outstanding" value={data.unpaid} icon={Clock} tone="#B08D57" />
        <Stat label={`${data.month_label || "This month"}`} value={data.month} icon={CalendarStar} tone="#8a6d0a" />
      </div>

      {data.by_photographer.length > 0 && (
        <div className="border border-[#E9DDC6] rounded-md overflow-x-auto bg-white" data-testid="commissions-by-photographer">
          <div className="bg-[#FAF9F6] px-4 py-3 eyebrow text-[#5C5A58]">By photographer</div>
          <table className="table-editorial text-sm min-w-[600px]">
            <thead>
              <tr className="text-left text-[#5C5A58] border-b border-[#E9DDC6]">
                <th className="p-4 font-medium">Photographer</th>
                <th className="p-4 font-medium">Bookings</th>
                <th className="p-4 font-medium">Total earned</th>
                <th className="p-4 font-medium">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {data.by_photographer.map((p, i) => (
                <tr key={p.photographer_id || `none-${i}`} className={i % 2 ? "bg-[#FBFAF7]" : "bg-white"} data-testid={`commission-ph-${p.photographer_id || "none"}`}>
                  <td className="p-4 font-medium">{p.photographer}</td>
                  <td className="p-4 font-mono-num">{p.count}</td>
                  <td className="p-4 font-mono-num font-semibold text-[#8a6d0a]">{money(p.total)}</td>
                  <td className="p-4 font-mono-num">{p.unpaid > 0 ? money(p.unpaid) : <span className="text-[#5C5A58]">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="border border-[#E9DDC6] rounded-md overflow-x-auto bg-white">
        <div className="bg-[#FAF9F6] px-4 py-3 eyebrow text-[#5C5A58]">All link-booking commissions</div>
        <table className="table-editorial text-sm min-w-[600px]" data-testid="commissions-rows">
          <thead>
            <tr className="text-left text-[#5C5A58] border-b border-[#E9DDC6]">
              <th className="p-4 font-medium">Property</th>
              <th className="p-4 font-medium">Earned by</th>
              <th className="p-4 font-medium">Booked date</th>
              <th className="p-4 font-medium">Commission</th>
              <th className="p-4 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#5C5A58]">No link-booking commissions yet. When agents book through a photographer's public page, the 10% commission shows here.</td></tr>}
            {data.rows.map((r, i) => (
              <tr key={r.property_id} className={r.paid ? "row-paid" : (i % 2 ? "bg-[#FBFAF7]" : "bg-white")} data-testid={`commission-row-${r.property_id}`}>
                <td className="p-4 font-medium">{r.title}
                  {r.booked_via_slug && <div className="text-xs text-[#9A968E] font-normal">via /p/{r.booked_via_slug}</div>}
                </td>
                <td className="p-4">{r.photographer}</td>
                <td className="p-4">{fmtDate(r.shoot_date || (r.created_at || "").slice(0, 10))}</td>
                <td className="p-4 font-mono-num font-semibold text-[#8a6d0a]">{money(r.amount)}</td>
                <td className="p-4">
                  {r.paid
                    ? <Badge className="bg-green-100 text-green-800 border-green-200">Paid</Badge>
                    : <Badge className="bg-[#B08D57]/15 text-[#8a6d0a] border-[#B08D57]/40">Outstanding</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
